/**
 * Preview viewport presets for the Forms Manager layout preview.
 *
 * The bespoke renderer's responsive behavior comes from Tailwind `md:` /
 * `max-md:` *viewport* media queries (see `oasis/bespoke/layout/nodes/Grid.tsx`,
 * `Flex.tsx`, `FieldRow.tsx`), not container queries. A width-constrained `div`
 * would squash the form while keeping its desktop column count, so the preview
 * renders inside an iframe that owns its own viewport — see `PreviewFrame`.
 */
export type PreviewViewport = 'laptop' | 'tablet' | 'mobile' | 'print';

/**
 * `null` means "fill the available width" (laptop). `print` is not a width at
 * all — it renders the server-side visit PDF (see `PrintPreview`) — so it sits
 * here as `null` and the component branches on the mode instead.
 *
 * Tablet is 768px — exactly Tailwind's `md` threshold — so the toggle shows the
 * form at the narrowest width that still counts as `md`, which is where layout
 * problems actually surface. Sitting on the threshold is only safe because
 * `PreviewFrame` grows the frame to fit its content: there is no vertical
 * scrollbar inside the frame to shave pixels off the layout viewport and
 * silently flip `md:` off.
 */
export const PREVIEW_VIEWPORT_WIDTHS: Record<PreviewViewport, number | null> = {
  laptop: null,
  tablet: 768,
  mobile: 390,
  print: null,
};

export const PREVIEW_VIEWPORT_LABELS: Record<PreviewViewport, string> = {
  laptop: 'Laptop',
  tablet: 'Tablet',
  mobile: 'Mobile',
  print: 'Print',
};

export const PREVIEW_VIEWPORT_ORDER: PreviewViewport[] = [
  'laptop',
  'tablet',
  'mobile',
  'print',
];
